import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import dataService from '../services/dataService';
import { toast } from 'react-toastify';
import {
    EyeIcon,
    XMarkIcon,
    PencilSquareIcon,
    PhoneIcon,
    ChatBubbleLeftEllipsisIcon,
    CurrencyRupeeIcon,
    CheckCircleIcon,
    InboxIcon,
    TrashIcon,
    UserIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const References = () => {
    const { user } = useAuth();
    const location = useLocation();
    const activeRole = sessionStorage.getItem('activeRole');
    const isActingAsChapterAdmin = user?.role === 'chapter_admin' && activeRole !== 'member';
    const isOrgAdmin = user?.role === 'admin';
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);
    const [references, setReferences] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [viewModal, setViewModal] = useState({ isOpen: false, data: null });
    const [statusModal, setStatusModal] = useState({ isOpen: false, data: null });
    const [statusForm, setStatusForm] = useState({ status: '', business_done_amount: '' });
    const [settings, setSettings] = useState(null);
    const [commentModal, setCommentModal] = useState({ isOpen: false, data: null, comments: [], newComment: '' });
    const commentInputRef = useRef(null);
    const [editModal, setEditModal] = useState({ isOpen: false, data: null });
    const [members, setMembers] = useState([]);
    const [formData, setFormData] = useState({
        referredTo: '',
        referralName: '',
        contactNo: '',
        companyName: '',
        referralDescription: '',
        referralFlag: 'Select Flag',
        status: 'Open',
        businessDoneAmount: ''
    });

    const handleReplyClick = (authorName) => {
        setCommentModal(prev => ({
            ...prev,
            newComment: `@${authorName} `
        }));
        setTimeout(() => {
            if (commentInputRef.current) {
                commentInputRef.current.focus();
            }
        }, 50);
    };

    // activeTab: 'received' or 'sent'
    let initialTab = location.state?.tab || 'received';
    if (initialTab !== 'received' && initialTab !== 'sent') initialTab = 'received';
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab]);

    // Update tab when location state changes
    useEffect(() => {
        if (location.state?.tab) {
            let tab = location.state.tab;
            if (tab !== 'received' && tab !== 'sent') tab = 'received';
            setActiveTab(tab);
        }
    }, [location.state]);

    useEffect(() => {
        fetchSettings();
        fetchReferences();
        fetchMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMembers = async () => {
        try {
            const res = await dataService.getUsers({ context: 'referral' });
            if (res.data.success) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                let allMembers = res.data.data.filter(u => {
                    const isApproved = u.status === 'approved';
                    const isValidRole = u.role === 'member' || u.role === 'chapter_admin';
                    const isNotCurrent = u.id !== user?.id;
                    let isNotExpired = true;
                    if (u.membership_renewal_date) {
                        const renewalDate = new Date(u.membership_renewal_date);
                        isNotExpired = renewalDate >= today;
                    }
                    return isApproved && isValidRole && isNotCurrent && isNotExpired;
                });

                // Apply "Give Referral Within Chapter Members Only" setting (Frontend safety)
                if (settings?.referrals_across_chapter && user?.chapter) {
                    allMembers = allMembers.filter(m => m.chapter === user.chapter);
                }

                setMembers(allMembers);
            }
        } catch (error) {
            console.error("Failed to fetch members");
        }
    };

    // Refresh members when settings are loaded
    useEffect(() => {
        if (settings) {
            fetchMembers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings]);

    const fetchSettings = async () => {
        try {
            const res = await dataService.getOrgSettings();
            if (res.data.success) {
                const orgSettings = typeof res.data.data.settings === 'string'
                    ? JSON.parse(res.data.data.settings || '{}')
                    : (res.data.data.settings || {});
                setSettings(orgSettings);
            }
        } catch (error) {
            console.error('Failed to load settings');
        }
    };

    const fetchReferences = async () => {
        try {
            setLoading(true);
            let res;
            // Admin and chapter_admin both see org-wide referrals (chapter_admin is filtered by backend)
            if (user?.role === 'admin' || isActingAsChapterAdmin) {
                res = await dataService.getReferences();
            } else {
                res = await dataService.getReceivedReferrals();
            }
            const allData = res.data.data;
            setReferences(allData);
        } catch (error) {
            console.error('Failed to load references', error);
            toast.error('Failed to load referrals');
        } finally {
            setLoading(false);
        }
    };

    const fetchComments = async (referralId) => {
        try {
            const res = await dataService.getReferralComments(referralId);
            setCommentModal(prev => ({ ...prev, comments: res.data.data }));
        } catch (error) {
            console.error('Failed to load comments');
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        const activeRef = commentModal.data;
        if (!activeRef) return;
        if (!commentModal.newComment.trim()) return;
        try {
            await dataService.addReferralComment(activeRef.id, commentModal.newComment);
            setCommentModal(prev => ({ ...prev, newComment: '' }));
            fetchComments(activeRef.id);
        } catch (error) {
            toast.error('Failed to add comment');
        }
    };

    const handleStatusUpdate = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (statusForm.status === 'Business Done') {
            if (!statusForm.business_done_amount || parseFloat(statusForm.business_done_amount) <= 0) {
                toast.error('Business Done Amount is required and must be greater than 0');
                return;
            }
        }

        setIsSubmitting(true);
        try {
            // Send only the clean fields needed for status update — do NOT spread the whole DB record
            // as it contains extra fields (sender_name, recipient_name, etc.) that confuse the backend
            const ref = statusModal.data;
            await dataService.updateReference(ref.id, {
                reference_name: ref.reference_name,
                ref_organization_name: ref.ref_organization_name,
                contact_email: '',
                contact_phone: ref.contact_phone,
                description: ref.description,
                referral_flag: ref.referral_flag,
                referred_to: ref.referred_to,
                status: statusForm.status,
                business_done_amount: statusForm.status === 'Business Done' ? parseFloat(statusForm.business_done_amount) : (ref.business_done_amount || 0)
            });
            toast.success('Status updated successfully');
            fetchReferences();
            setStatusModal({ isOpen: false, data: null });
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update status');
        } finally {
            setIsSubmitting(false);
        }
    };

    const canEdit = (ref) => {
        return activeTab === 'received' && Number(ref.referred_to) === Number(user?.id);
    };

    const canDelete = (ref) => {
        return false;
    };

    const handleDeleteReference = async (id) => {
        if (window.confirm("Are you sure you want to delete this referral?")) {
            try {
                await dataService.deleteReference(id);
                toast.success('Referral deleted successfully');
                fetchReferences();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to delete referral');
            }
        }
    };

    const handleEditReferral = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            const payload = {
                reference_name: formData.referralName,
                ref_organization_name: formData.companyName,
                contact_email: '',
                contact_phone: formData.contactNo,
                description: formData.referralDescription,
                status: formData.status,
                referral_flag: formData.referralFlag,
                referred_to: formData.referredTo,
                business_done_amount: formData.businessDoneAmount
            };
            await dataService.updateReference(editModal.data.id, payload);
            toast.success('Referral updated successfully');
            setEditModal({ isOpen: false, data: null });
            fetchReferences();
        } catch (error) {
            console.error("Update error:", error);
            const msg = error.response?.data?.message || 'Failed to update referral';
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const openEdit = (ref) => {
        setFormData({
            referredTo: ref.referred_to || '',
            referralName: ref.reference_name || '',
            contactNo: ref.contact_phone || '',
            companyName: ref.ref_organization_name || '',
            referralDescription: ref.description || '',
            referralFlag: ref.referral_flag || 'Select Flag',
            status: ref.status || 'Open',
            businessDoneAmount: ref.business_done_amount || ''
        });
        setEditModal({ isOpen: true, data: ref });
    };

    const filteredReferences = references.filter(ref => {
        // Exclude referrals sent by the logged-in user from the received view
        if (activeTab === 'received' && Number(ref.user_id) === Number(user?.id)) {
            return false;
        }

        if (!isActingAsChapterAdmin) return true;
        
        const userChap = String(user?.chapter || '').trim().toLowerCase();
        const recChap = String(ref.recipient_chapter || '').trim().toLowerCase();
        const senChap = String(ref.sender_chapter || '').trim().toLowerCase();

        if (activeTab === 'received') {
            return recChap === userChap;
        } else if (activeTab === 'sent') {
            return senChap === userChap;
        }
        return true;
    });

    const itemsPerPage = 12;
    const totalPages = Math.ceil(filteredReferences.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedReferences = filteredReferences.slice(startIndex, endIndex);

    return (
        <div className="space-y-6 h-full flex flex-col animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight italic">Referrals List</h1>
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mt-1">Status and management for your chapter's referrals</p>
                </div>
                {isActingAsChapterAdmin && (
                    <div className="flex p-1 bg-gray-100 rounded-xl overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('received')}
                            className={clsx(
                                "px-6 py-2.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all whitespace-nowrap",
                                activeTab === 'received' ? "bg-white text-gray-900 shadow-sm" : "text-gray-400 hover:text-gray-600"
                            )}
                        >
                            Received Referrals
                        </button>
                        <button
                            onClick={() => setActiveTab('sent')}
                            className={clsx(
                                "px-6 py-2.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all whitespace-nowrap",
                                activeTab === 'sent' ? "bg-white text-gray-900 shadow-sm" : "text-gray-400 hover:text-gray-600"
                            )}
                        >
                            Sent Referrals
                        </button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex flex-1 items-center justify-center py-20 bg-white rounded-[2.5rem] shadow-sm border border-gray-100/50">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-100 border-t-[#4eb7f5]"></div>
                </div>
            ) : (
                <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden flex-1 flex flex-col">
                    {references.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-20 text-gray-400">
                            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                                <InboxIcon className="w-10 h-10 opacity-20" />
                            </div>
                            <p className="text-xl font-black uppercase tracking-widest opacity-30 italic px-8 text-center">No referrals received yet</p>
                        </div>
                    ) : (
                        <>
                            {/* Unified Premium Card Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                                {paginatedReferences.map((ref) => (
                                    <div
                                        key={ref.id}
                                        className="group relative bg-white border-2 border-slate-100 rounded-3xl p-6 hover:border-primary-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                                    >
                                        <div>
                                            {/* Header */}
                                            <div className="flex items-start justify-between gap-4 mb-4">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center border-2 border-white shadow-md ring-2 ring-slate-50 flex-shrink-0">
                                                        <UserIcon className="w-6 h-6 text-primary-600" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h3 className="text-sm font-black text-slate-900 truncate">
                                                            {ref.sender_name}
                                                        </h3>
                                                        <div className="flex items-center gap-1 text-[10px] text-slate-400 font-bold uppercase mt-0.5">
                                                            <span>{ref.sender_chapter || 'N/A'}</span>
                                                            <ArrowRightIcon className="w-3 h-3 text-slate-300 shrink-0" />
                                                            <span className="text-slate-600 font-black truncate max-w-[80px]" title={ref.recipient_name || ref.referred_to}>
                                                                {ref.recipient_name || ref.referred_to}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className={clsx(
                                                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider border shrink-0",
                                                    ref.status === 'Business Done' ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
                                                        ref.status === 'Closed' ? "bg-slate-50 text-slate-500 border-slate-100" :
                                                            "bg-blue-50 text-blue-700 border-blue-100"
                                                )}>
                                                    <div className={clsx("w-1.5 h-1.5 rounded-full",
                                                        ref.status === 'Business Done' ? "bg-emerald-500 animate-pulse" :
                                                            ref.status === 'Closed' ? "bg-slate-400" : "bg-blue-500"
                                                    )} />
                                                    {ref.status || 'Open'}
                                                </span>
                                            </div>

                                            {/* Details Grid */}
                                            <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        Recipient Chapter
                                                    </p>
                                                    <p className="text-xs font-bold text-slate-700 truncate">
                                                        {ref.recipient_chapter || 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        Date Referred
                                                    </p>
                                                    <p className="text-xs font-bold text-slate-700">
                                                        {new Date(ref.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </p>
                                                </div>
                                                <div className="col-span-2">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        Priority
                                                    </p>
                                                    <span className={clsx(
                                                        "inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wide border mt-0.5",
                                                        ref.referral_flag === 'Hot' ? "bg-red-50 text-red-700 border-red-100" :
                                                            ref.referral_flag === 'Warm' ? "bg-amber-50 text-amber-700 border-amber-100" :
                                                                "bg-blue-50 text-blue-700 border-blue-100"
                                                    )}>
                                                        {ref.referral_flag || 'Medium'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Actions Footer */}
                                        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100 mt-auto">
                                            {/* View Details (Only show if not Chapter Admin with settings block enabled) */}
                                            {!(isActingAsChapterAdmin && settings?.chapter_referral_thirdparty_data === true) && (
                                                <button
                                                    onClick={() => {
                                                        setViewModal({ isOpen: true, data: ref });
                                                        setCommentModal({ isOpen: false, data: ref, comments: [], newComment: '' });
                                                        fetchComments(ref.id);
                                                    }}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all shadow-sm active:scale-95"
                                                    title="View Details"
                                                >
                                                    <EyeIcon className="w-4 h-4" />
                                                </button>
                                            )}

                                            {/* Other Actions: Only for Members (Hidden for Org Admins and Chapter Admins) */}
                                            {!isOrgAdmin && !isActingAsChapterAdmin && (
                                                <button
                                                    onClick={() => {
                                                        setCommentModal({ isOpen: true, data: ref, comments: [], newComment: '' });
                                                        setViewModal({ isOpen: false, data: null });
                                                        fetchComments(ref.id);
                                                    }}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-[#4eb7f5] hover:bg-blue-50 hover:border-[#4eb7f5] transition-all shadow-sm active:scale-95"
                                                    title="View Comments"
                                                >
                                                    <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                             {/* Edit Referral */}
                                             {ref.status !== 'Business Done' && canEdit(ref) && (
                                                 <button
                                                     onClick={() => {
                                                         const isRecipient = Number(ref.referred_to) === Number(user?.id);
                                                         const isAdmin = user?.role === 'admin';
                                                         const isCreator = Number(ref.user_id) === Number(user?.id);

                                                         if (isRecipient && !isAdmin && !isCreator) {
                                                             setStatusModal({ isOpen: true, data: ref });
                                                             setStatusForm({
                                                                 status: ref.status || 'Open',
                                                                 business_done_amount: ref.business_done_amount || ''
                                                             });
                                                         } else {
                                                             openEdit(ref);
                                                         }
                                                     }}
                                                     className="p-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-emerald-600 hover:bg-emerald-50 hover:border-emerald-200 transition-all shadow-sm active:scale-95"
                                                     title="Edit Referral"
                                                 >
                                                     <PencilSquareIcon className="w-4 h-4" />
                                                 </button>
                                             )}

                                             {/* Delete Referral */}
                                             {ref.status !== 'Business Done' && canDelete(ref) && (
                                                 <button
                                                     onClick={() => handleDeleteReference(ref.id)}
                                                     className="p-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200 transition-all shadow-sm active:scale-95"
                                                     title="Delete Referral"
                                                 >
                                                     <TrashIcon className="w-4 h-4" />
                                                 </button>
                                             )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        {/* Pagination Controls */}
                        <div className="flex flex-col md:flex-row items-center justify-between px-8 py-5 border-t border-slate-100 gap-4 bg-gray-50/50">
                            <div className="text-sm font-semibold text-slate-600">
                                Showing <span className="font-black text-slate-900">{Math.min(endIndex, filteredReferences.length)}</span> of{' '}
                                <span className="font-black text-slate-900">{filteredReferences.length}</span> {filteredReferences.length === 1 ? 'referral' : 'referrals'}
                            </div>

                            {filteredReferences.length > itemsPerPage && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === 1
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                            : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                            }`}
                                    >
                                        Previous
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {[...Array(totalPages)].map((_, index) => {
                                            const pageNum = index + 1;
                                            if (
                                                pageNum === 1 ||
                                                pageNum === totalPages ||
                                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${currentPage === pageNum
                                                            ? 'bg-[#4eb7f5] text-white shadow-lg shadow-blue-100'
                                                            : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                                return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === totalPages
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                            : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                            }`}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
                </div>
            )}

            {/* Premium Centralized Modal Container */}            {(mounted && (viewModal.isOpen || statusModal.isOpen || commentModal.isOpen || editModal.isOpen)) && createPortal(
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className={clsx(
                        "bg-white rounded-[2rem] shadow-2xl w-full max-h-[90vh] overflow-y-auto relative border border-gray-100/50",
                        viewModal.isOpen ? "max-w-4xl" : (commentModal.isOpen || editModal.isOpen) ? "max-w-2xl" : "max-w-lg"
                    )}>
                        {/* Global Close Button */}
                        <button
                            onClick={() => {
                                setViewModal({ isOpen: false, data: null });
                                setStatusModal({ isOpen: false, data: null });
                                setCommentModal({ isOpen: false, data: null, comments: [], newComment: '' });
                                setEditModal({ isOpen: false, data: null });
                            }}
                            className="absolute top-4 right-6 z-10 p-2 rounded-2xl bg-white/20 text-white hover:bg-white/30 transition-all active:scale-90 shadow-sm border border-white/20"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>

                        {/* View Content */}
                        {viewModal.isOpen && (
                            <div className="animate-in zoom-in-95 duration-400">
                                <div className="bg-gradient-to-r from-[#4eb7f5] to-[#3da6e4] px-6 py-6 mb-4">
                                    <div className="flex items-center gap-4">
                                        <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-md border border-white/20">
                                            <EyeIcon className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-black text-white tracking-tight italic">Referral Details & Timeline</h2>
                                            <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest">Opportunity summary and discussion</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {/* Left Column: Details */}
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-[#4eb7f5] uppercase tracking-widest block ml-1">Client Name</label>
                                                    <p className="text-sm font-black text-gray-900 bg-gray-50 px-4 py-2.5 rounded-xl border border-gray-100">{viewModal.data?.reference_name}</p>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-[#4eb7f5] uppercase tracking-widest block ml-1">Organization</label>
                                                    <p className="text-sm font-black text-gray-900 bg-gray-50 px-4 py-2.5 rounded-xl border border-gray-100">{viewModal.data?.ref_organization_name || 'N/A'}</p>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 flex items-center gap-1.5">
                                                        <PhoneIcon className="w-3 h-3 text-[#4eb7f5]" />
                                                        Contact Number
                                                    </label>
                                                    <p className="text-[13px] font-bold text-gray-800 px-1 ml-1">{viewModal.data?.contact_phone || 'Not provided'}</p>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Source Member</label>
                                                    <p className="text-[13px] font-black text-blue-600 px-1 ml-1 italic">{viewModal.data?.sender_name} ({viewModal.data?.sender_chapter || 'N/A'})</p>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Date Received</label>
                                                    <p className="text-[13px] font-bold text-gray-800 px-1 ml-1">{new Date(viewModal.data?.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Referred To</label>
                                                    <p className="text-[13px] font-black text-emerald-600 px-1 ml-1 italic">{viewModal.data?.recipient_name || viewModal.data?.referred_to || 'N/A'} ({viewModal.data?.recipient_chapter || 'N/A'})</p>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-[#4eb7f5] uppercase tracking-widest block ml-1">Priority</label>
                                                    <p className="text-sm font-black text-gray-900 bg-gray-50 px-4 py-2.5 rounded-xl border border-gray-100">{viewModal.data?.referral_flag || 'Medium'}</p>
                                                </div>
                                            </div>

                                            {viewModal.data?.description && (
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 flex items-center gap-1.5">
                                                        <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5 text-[#4eb7f5]" />
                                                        Notes & Context
                                                    </label>
                                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 italic relative">
                                                        <p className="text-[13px] font-medium text-gray-700 leading-relaxed px-2">
                                                            {viewModal.data.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {viewModal.data?.status === 'Business Done' && (
                                                <div className="p-4 bg-emerald-50 rounded-2xl border-2 border-emerald-100 flex items-center justify-between">
                                                    <div>
                                                        <label className="text-[10px] font-black text-emerald-600 uppercase tracking-widest block ml-1">Revenue Generated</label>
                                                        <div className="flex items-center gap-1 text-xl font-black text-emerald-900 mt-1">
                                                            <CurrencyRupeeIcon className="w-5 h-5" />
                                                            {parseFloat(viewModal.data.business_done_amount || 0).toLocaleString('en-IN')}
                                                        </div>
                                                    </div>
                                                    <div className="h-10 w-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200 rotate-6 transition-transform hover:rotate-0">
                                                        <CheckCircleIcon className="w-6 h-6 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Right Column: Comments/Timeline */}
                                        <div className="border-t lg:border-t-0 lg:border-l border-gray-100 pt-6 lg:pt-0 lg:pl-8 flex flex-col h-full justify-between">
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <ChatBubbleLeftEllipsisIcon className="w-4.5 h-4.5 text-[#4eb7f5]" />
                                                    <h3 className="text-sm font-black text-gray-900 tracking-tight uppercase">Comments Timeline</h3>
                                                </div>
                                                
                                                <div className="min-h-[200px] max-h-[300px] overflow-y-auto mb-4 pr-2 flex flex-col gap-4 scrollbar-thin scrollbar-thumb-gray-200">
                                                    {commentModal.comments.length === 0 ? (
                                                        <div className="flex flex-1 flex-col items-center justify-center py-10 text-gray-400">
                                                            <p className="text-xs font-black uppercase tracking-widest opacity-20 italic">No activity recorded yet</p>
                                                        </div>
                                                    ) : (
                                                        commentModal.comments.map(c => (
                                                            <div key={c.id} className={clsx("max-w-[85%] rounded-[1.5rem] p-4 shadow-sm relative transition-all", c.user_id === user.id ? "ml-auto bg-blue-50 text-blue-900 border border-blue-100" : "mr-auto bg-gray-50 text-gray-800 border border-gray-100")}>
                                                                <div className="flex justify-between items-center mb-1.5 gap-6">
                                                                    <p className="text-[9px] font-black uppercase tracking-widest text-blue-600">{c.author_name}</p>
                                                                    <p className="text-[9px] text-gray-400 font-bold">{new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                                </div>
                                                                <p className="text-xs leading-relaxed font-bold">{c.comment}</p>
                                                                {c.user_id !== user.id && (
                                                                    <div className="flex justify-start mt-1">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleReplyClick(c.author_name)}
                                                                            className="text-[9px] font-bold text-gray-500 hover:text-blue-600 transition-colors"
                                                                        >
                                                                            Reply
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>

                                                <form onSubmit={handleAddComment} className="flex gap-4 items-center">
                                                    <input
                                                        ref={commentInputRef}
                                                        type="text"
                                                        value={commentModal.newComment}
                                                        onChange={(e) => setCommentModal(prev => ({ ...prev, newComment: e.target.value }))}
                                                        placeholder="Add a comment or reply..."
                                                        className="flex-1 bg-gray-50 rounded-xl border-transparent border-0 px-4 py-3 text-sm focus:ring-4 focus:ring-blue-50 focus:bg-white transition-all shadow-inner"
                                                    />
                                                    <button
                                                        type="submit"
                                                        disabled={!commentModal.newComment.trim()}
                                                        className="bg-gray-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 disabled:opacity-20 transition-all shadow-lg active:scale-95"
                                                    >
                                                        Post
                                                    </button>
                                                </form>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Modal Footer Close Button */}
                                    <div className="pt-6 mt-8 border-t border-gray-100 flex justify-end">
                                        <button
                                            onClick={() => setViewModal({ isOpen: false, data: null })}
                                            className="px-6 py-2.5 bg-gray-900 text-white rounded-xl text-[12px] font-black uppercase tracking-widest hover:bg-gray-800 transition-all active:scale-95 shadow-lg shadow-gray-200"
                                        >
                                            Close Detail
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )})}

                        {/* Status Content */}
                        {statusModal.isOpen && (
                            <div className="p-10 animate-in zoom-in-95 duration-400">
                                <div className="mb-10">
                                    <h3 className="text-3xl font-black text-slate-900 tracking-tight italic">Update Status</h3>
                                    <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mt-1">Manage the lifecycle of this referral</p>
                                </div>

                                <form onSubmit={handleStatusUpdate} className="space-y-8">
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Current Status</label>
                                                <select
                                                    value={statusForm.status}
                                                    onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                                                    className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-[1.5rem] font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all"
                                                >
                                                    <option value="Open">Open</option>
                                                    <option value="Contacted">Contacted</option>
                                                    <option value="In Progress">In Progress</option>
                                                    <option value="Business Done">Business Done</option>
                                                    <option value="Closed">Closed</option>
                                                </select>
                                            </div>

                                            {statusForm.status === 'Business Done' && (
                                                <div className="space-y-2 animate-in slide-in-from-top-4 duration-300">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Business Done Amount (₹)</label>
                                                    <div className="relative">
                                                        <CurrencyRupeeIcon className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                                        <input
                                                            type="number"
                                                            value={statusForm.business_done_amount}
                                                            onChange={(e) => setStatusForm({ ...statusForm, business_done_amount: e.target.value })}
                                                            className="w-full pl-14 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-[1.5rem] font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex gap-4 pt-6 border-t border-gray-50">
                                        <button
                                            type="button"
                                            onClick={() => setStatusModal({ isOpen: false, data: null })}
                                            className="flex-1 px-8 py-4 bg-gray-50 text-gray-500 rounded-[1.5rem] text-[13px] font-black uppercase tracking-widest hover:bg-gray-100 transition-all active:scale-95"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="flex-1 px-8 py-4 bg-[#4eb7f5] text-white rounded-[1.5rem] text-[13px] font-black uppercase tracking-widest hover:bg-[#3da6e4] transition-all shadow-xl shadow-blue-100 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                                        >
                                            {isSubmitting ? 'Updating...' : 'Update Referral'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Comment Content */}
                        {commentModal.isOpen && (
                            <div className="animate-in zoom-in-95 duration-400">
                                <div className="bg-gradient-to-r from-[#4eb7f5] to-[#3da6e4] px-10 py-8 mb-4">
                                    <div className="flex items-center gap-4 text-white">
                                        <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-md border border-white/20">
                                            <ChatBubbleLeftEllipsisIcon className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h2 className="text-3xl font-black italic text-white tracking-tight">Timeline</h2>
                                            <p className="text-[11px] font-bold text-white/70 uppercase tracking-widest">Discussion & updates log</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="min-h-[250px] max-h-[400px] overflow-y-auto mb-6 pr-2 flex flex-col gap-4 scrollbar-thin scrollbar-thumb-gray-200">
                                        {commentModal.comments.length === 0 ? (
                                            <div className="flex flex-1 flex-col items-center justify-center py-10 text-gray-400">
                                                <p className="text-xs font-black uppercase tracking-widest opacity-20 italic">No activity recorded yet</p>
                                            </div>
                                        ) : (
                                            commentModal.comments.map(c => (
                                                <div key={c.id} className={clsx("max-w-[85%] rounded-[1.5rem] p-4 shadow-sm relative transition-all", c.user_id === user.id ? "ml-auto bg-blue-50 text-blue-900 border border-blue-100" : "mr-auto bg-gray-50 text-gray-800 border border-gray-100")}>
                                                    <div className="flex justify-between items-center mb-1.5 gap-6">
                                                        <p className="text-[9px] font-black uppercase tracking-widest text-blue-600">{c.author_name}</p>
                                                        <p className="text-[9px] text-gray-400 font-bold">{new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                    </div>
                                                    <p className="text-xs leading-relaxed font-bold">{c.comment}</p>
                                                    {c.user_id !== user.id && (
                                                        <div className="flex justify-start mt-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleReplyClick(c.author_name)}
                                                                className="text-[9px] font-bold text-gray-500 hover:text-blue-600 transition-colors"
                                                            >
                                                                Reply
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <form onSubmit={handleAddComment} className="flex gap-4 items-center">
                                        <input
                                            ref={commentInputRef}
                                            type="text"
                                            value={commentModal.newComment}
                                            onChange={(e) => setCommentModal(prev => ({ ...prev, newComment: e.target.value }))}
                                            placeholder="Add a comment or reply..."
                                            className="flex-1 bg-gray-50 rounded-xl border-transparent border-0 px-4 py-3 text-sm focus:ring-4 focus:ring-blue-50 focus:bg-white transition-all shadow-inner"
                                        />
                                        <button
                                            type="submit"
                                            disabled={!commentModal.newComment.trim()}
                                            className="bg-gray-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 disabled:opacity-20 transition-all shadow-lg active:scale-95"
                                        >
                                            Post
                                        </button>
                                    </form>

                                    <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setCommentModal({ isOpen: false, data: null, comments: [], newComment: '' })}
                                            className="px-6 py-2.5 bg-gray-100 text-gray-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-200 transition-all"
                                        >
                                            Close Timeline
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Edit Form */}
                        {editModal.isOpen && (
                            <div className="p-10 animate-in zoom-in-95 duration-400">
                                <div className="mb-10">
                                    <h2 className="text-3xl font-black text-gray-900 tracking-tight italic">
                                        Edit Referral
                                    </h2>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                                        Modify shared opportunity details
                                    </p>
                                </div>

                                <form onSubmit={handleEditReferral} className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Referred To Member</label>
                                            <select
                                                required
                                                disabled={editModal.isOpen}
                                                value={formData.referredTo}
                                                onChange={(e) => setFormData({ ...formData, referredTo: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            >
                                                <option value="">Select a member</option>
                                                {members.map(m => (
                                                    <option key={m.id} value={m.id}>{m.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Client Full Name</label>
                                            <input
                                                required
                                                type="text"
                                                placeholder="e.g. John Doe"
                                                value={formData.referralName}
                                                onChange={(e) => setFormData({ ...formData, referralName: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Contact Number</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. +91 99999 00000"
                                                value={formData.contactNo}
                                                onChange={(e) => setFormData({ ...formData, contactNo: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Priority Level</label>
                                            <select
                                                value={formData.referralFlag}
                                                onChange={(e) => setFormData({ ...formData, referralFlag: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            >
                                                <option value="Select Flag">Select Level</option>
                                                <option value="Hot">Hot (High)</option>
                                                <option value="Warm">Warm (Medium)</option>
                                                <option value="Cold">Cold (Low)</option>
                                            </select>
                                        </div>
                                        <div className="col-span-1 md:col-span-2 space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Company / Organization</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. Acme Corp"
                                                value={formData.companyName}
                                                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="col-span-1 md:col-span-2 space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Additional Notes</label>
                                            <textarea
                                                rows="3"
                                                placeholder="Provide any context to help the receiver..."
                                                value={formData.referralDescription}
                                                onChange={(e) => setFormData({ ...formData, referralDescription: e.target.value })}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm resize-none h-32"
                                            />
                                        </div>
                                        <div className="col-span-1 md:col-span-2 space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Current Status</label>
                                            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-6 py-5 border border-gray-100 italic">
                                                <span className="text-[10px] font-black uppercase text-blue-600 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-100">
                                                    {formData.status || 'Open'}
                                                </span>
                                                <p className="text-[11px] font-bold text-gray-400">Status updates are restricted to the receiver only.</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4 pt-6">
                                        <button
                                            type="button"
                                            onClick={() => setEditModal({ isOpen: false, data: null })}
                                            className="flex-1 px-8 py-5 border-2 border-gray-100 text-gray-500 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="flex-[2] px-8 py-5 bg-gray-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-2xl shadow-gray-300 hover:bg-[#4eb7f5] hover:shadow-[#4eb7f5]/20 transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                                        >
                                            {isSubmitting ? 'Updating...' : 'Update Details'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default References;
